import React, { useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Mail, Lock, Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import GoogleIcon from "@/components/GoogleIcon";
import FacebookIcon from "@/components/FacebookIcon";
import AppleIcon from "@/components/AppleIcon";
import { useI18n } from "@/lib/I18nContext";
import { safeReturnTo } from "@/lib/authReturnTo";

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      const dest = safeReturnTo();
      window.location.href = dest === "/" ? "/overview" : dest;
    } catch (err) {
      setError(err.message || t("auth.invalid_credentials", "Email o contraseña inválidos"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("google", dest === "/" ? "/overview" : dest);
  };

  const handleFacebook = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("facebook", dest === "/" ? "/overview" : dest);
  };

  const handleApple = () => {
    const dest = safeReturnTo();
    base44.auth.loginWithProvider("apple", dest === "/" ? "/overview" : dest);
  };

  return (
    <AuthLayout
      icon={LogIn}
      title={t("auth.welcome_back", "Welcome back")}
      subtitle={t("auth.log_in_to_account", "Log in to your account")}
      footer={
        <>
          {t("auth.dont_have_account", "Don't have an account?")}{" "}
          <Link to="/register" className="text-primary font-medium hover:underline">
            {t("auth.create_one", "Create one")}
          </Link>
        </>
      }
    >
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-2"
        onClick={handleGoogle}
      >
        <GoogleIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_google", "Continue with Google")}
      </Button>
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-2"
        onClick={handleFacebook}
      >
        <FacebookIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_facebook", "Continue with Facebook")}
      </Button>
      <Button
        variant="outline"
        className="w-full h-12 text-sm font-medium mb-6"
        onClick={handleApple}
      >
        <AppleIcon className="w-5 h-5 mr-2" />
        {t("auth.continue_with_apple", "Continue with Apple")}
      </Button>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-3 text-muted-foreground">{t("auth.or", "or")}</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email", "Email")}</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("auth.password", "Password")}</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline">
              {t("auth.forgot_password", "Forgot password?")}
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>
        <Button type="submit" className="w-full h-12 font-medium" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.logging_in", "Logging in...")}
            </>
          ) : (
            t("auth.log_in", "Log in")
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}